import { Button } from "@/components/ui/button";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import { AG_GRID_LOCALE_RU } from "@/lib/ag-grid-locale-ru";
import {
	diagnosticsClient,
	formatTimestamp,
	shortId,
} from "@/lib/diagnostics";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { useGridLayout } from "@/lib/use-grid-layout";

import type { ColDef } from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Loader2, Plus, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useGridSelection } from "@/hooks/use-grid-selection";
import { useServerGrid } from "@/lib/use-server-grid";

export const Route = createFileRoute("/diagnostics/exports")({
	component: DiagnosticsExportsPage,
	validateSearch: (search: Record<string, unknown>) => ({
		create: search.create === "1" ? "1" : undefined,
	}),
});

interface ExportRow {
	id: string;
	employeeId: string;
	requestedBy: string;
	dateFrom: string;
	dateTo: string;
	bundlePrefix: string;
	status: string;
	createdAt: unknown;
}

function DiagnosticsExportsPage() {
	const { t } = useTranslation("diagnostics");
	useBreadcrumbs([{ label: t("title"), href: "/diagnostics" }, { label: t("sidebar_exports") }]);
	const search = Route.useSearch();
	const { onGridReady, onStateChanged } = useGridLayout("diagnostics-exports");
	const [createOpen, setCreateOpen] = useState(search.create === "1");
	const { selectedRows, onSelectionChanged, ActionBar } =
		useGridSelection<ExportRow>();

	const { gridRef, gridProps, totalRows, refreshGrid } = useServerGrid<ExportRow>({
		fetchRows: async ({ pageSize, pageToken }) => {
			const resp = await diagnosticsClient.listExportJobs({
				status: "",
				page: { pageSize, pageToken },
			});
			return {
				rows: (resp.jobs ?? []) as ExportRow[],
				nextPageToken: resp.page?.nextPageToken,
				totalCount: resp.page?.totalCount ?? 0,
			};
		},
		colToField: {
			employeeId: "employee_id",
			bundlePrefix: "bundle_prefix",
			createdAt: "created_at",
		},
	});

	// ── Column definitions ──
	const columnDefs = useMemo<ColDef<ExportRow>[]>(
		() => [
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 36,
				maxWidth: 36,
				pinned: "left",
				suppressHeaderMenuButton: true,
				resizable: false,
			},
			{
				field: "id",
				headerName: "ID",
				width: 110,
				cellClass: "font-mono",
				filter: false,
				valueFormatter: (p) => shortId(p.value),
			},
			{
				field: "employeeId",
				headerName: t("exports_col_employee"),
				width: 110,
				cellClass: "font-mono",
				valueFormatter: (p) => shortId(p.value),
			},
			{
				colId: "dateRange",
				headerName: t("exports_col_range"),
				width: 180,
				filter: false,
				valueGetter: (p) => {
					const d = p.data;
					if (!d) return "";
					return `${d.dateFrom} - ${d.dateTo}`;
				},
			},
			{
				field: "bundlePrefix",
				headerName: t("exports_col_bundle"),
				width: 160,
				valueFormatter: (p) => p.value || "\u2014",
			},
			{
				field: "status",
				headerName: t("exports_col_status"),
				width: 120,
				filter: false,
				cellRenderer: (p: { value: string }) => {
					const color =
						p.value === "failed"
							? "hsl(var(--destructive))"
							: p.value === "ready"
								? "hsl(var(--chart-2))"
								: p.value === "processing"
									? "hsl(var(--chart-4))"
									: "hsl(var(--muted-foreground))";
					return (
						<span style={{ display: "inline-block", padding: "1px 8px", borderRadius: 9999, fontSize: 11, lineHeight: "18px", border: `1px solid color-mix(in srgb, ${color} 20%, transparent)`, color }}>
							{t(`enum_status_${p.value}`, { defaultValue: p.value })}
						</span>
					);
				},
			},
			{
				field: "createdAt",
				headerName: t("exports_col_created"),
				width: 140,
				filter: "agDateColumnFilter",
				valueFormatter: (p) => formatTimestamp(p.value),
			},
		],
		[t],
	);

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: true,
			resizable: true,
			filter: true,
			filterParams: { buttons: ["apply", "reset"], closeOnApply: true },
		}),
		[],
	);

	// ── Status bar ──
	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "count",
				content: `${totalRows} ${t("exports_status_count", { defaultValue: "заданий" })}`,
				align: "left" as const,
			},
			...(selectedRows.length > 0
				? [
						{
							id: "selection",
							content: t("rows_selected", {
								count: selectedRows.length,
								defaultValue: `${selectedRows.length} выбрано`,
							}),
							align: "right" as const,
						},
					]
				: []),
		],
		[totalRows, selectedRows.length, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	return (
		<>
			<div className="flex items-center gap-2 px-4 py-2 shrink-0">
				<div className="relative flex-1">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("search_placeholder", { defaultValue: "Поиск..." })}
						onChange={(e) =>
							gridRef.current?.api?.setGridOption("quickFilterText", e.target.value)
						}
						className="pl-9 h-9"
					/>
				</div>
				<Button size="sm" className="h-9" onClick={() => setCreateOpen(true)}>
					<Plus className="h-4 w-4 mr-1" />
					{t("exports_create")}
				</Button>
			</div>
			<div
				className="flex-1 ag-theme-quartz h-full"
				style={{ width: "100%" }}
			>
				<AgGridReact<ExportRow>
					ref={gridRef}
					{...gridProps}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					localeText={AG_GRID_LOCALE_RU}
					rowSelection="multiple"
					onSelectionChanged={onSelectionChanged}
					onGridReady={onGridReady}
					onSortChanged={() => { onStateChanged(); refreshGrid(); }}
					onFilterChanged={() => { onStateChanged(); refreshGrid(); }}
					onColumnMoved={onStateChanged}
					onColumnResized={onStateChanged}
					onColumnVisible={onStateChanged}
					suppressRowClickSelection
					animateRows={false}
					rowHeight={28}
					headerHeight={32}
					getRowId={(p) => p.data.id}
					overlayLoadingTemplate={`<span style="font-size:12px;color:hsl(var(--muted-foreground))">${t("loading", { defaultValue: "Загрузка" })}...</span>`}
				/>
			</div>

			<ActionBar />
			<Dialog open={createOpen} onOpenChange={setCreateOpen}>
				<DialogContent>
					<CreateExportJobForm
						onSuccess={() => {
							setCreateOpen(false);
							refreshGrid();
							toast.success(t("toast_export_created"));
						}}
						onCancel={() => setCreateOpen(false)}
					/>
				</DialogContent>
			</Dialog>
		</>
	);
}

function CreateExportJobForm({
	onSuccess,
	onCancel,
}: { onSuccess: () => void; onCancel: () => void }) {
	const { t } = useTranslation("diagnostics");
	const [employeeId, setEmployeeId] = useState("");
	const [observedPartyId, setObservedPartyId] = useState("");
	const [dateFrom, setDateFrom] = useState("");
	const [dateTo, setDateTo] = useState("");
	const [bundlePrefix, setBundlePrefix] = useState("");

	const mutation = useMutation({
		mutationFn: () =>
			diagnosticsClient.createExportJob({
				employeeId,
				observedPartyId,
				dateFrom,
				dateTo,
				bundlePrefix,
			}),
		onSuccess,
		onError: (err: Error) =>
			toast.error(t("toast_error_generic"), { description: err.message }),
	});

	return (
		<>
			<DialogHeader>
				<DialogTitle>{t("exports_create_title")}</DialogTitle>
				<DialogDescription>{t("exports_create_description")}</DialogDescription>
			</DialogHeader>
			<div className="space-y-4 py-4">
				<div className="space-y-2">
					<Label>{t("exports_employee_id")}</Label>
					<Input
						placeholder="UUID"
						value={employeeId}
						onChange={(event) => setEmployeeId(event.target.value)}
					/>
				</div>
				<div className="space-y-2">
					<Label>{t("exports_observed_party_id")}</Label>
					<Input
						placeholder="UUID"
						value={observedPartyId}
						onChange={(event) => setObservedPartyId(event.target.value)}
					/>
				</div>
				<div className="space-y-2">
					<Label>{t("exports_date_from")}</Label>
					<DateTimePicker value={dateFrom} onChange={(v) => setDateFrom(v)} />
				</div>
				<div className="space-y-2">
					<Label>{t("exports_date_to")}</Label>
					<DateTimePicker value={dateTo} onChange={(v) => setDateTo(v)} />
				</div>
				<div className="space-y-2">
					<Label>{t("exports_bundle_prefix")}</Label>
					<Input
						value={bundlePrefix}
						onChange={(event) => setBundlePrefix(event.target.value)}
					/>
				</div>
			</div>
			<DialogFooter>
				<Button variant="outline" onClick={onCancel}>
					{t("cancel")}
				</Button>
				<Button
					onClick={() => mutation.mutate()}
					disabled={mutation.isPending || !dateFrom || !dateTo}
				>
					{mutation.isPending ? (
						<>
							<Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
							{t("exports_creating")}
						</>
					) : (
						t("exports_create")
					)}
				</Button>
			</DialogFooter>
		</>
	);
}
